import { useEffect, useRef } from "react";
import {
  Canvas as FabricCanvas,
  Textbox,
  FabricImage,
  Rect,
  Text,
} from "fabric";
import { getTemplatePositions } from "@/config/templatePositions";
import sash1 from "@/assets/sash-template-1.png";
import sash2 from "@/assets/sash-template-2.png";
import sash3 from "@/assets/sash-template-3.png";
import sash4 from "@/assets/sash-template-4.png";
import sash5 from "@/assets/sash-template-5.png";
import sash6 from "@/assets/sash-template-6.png";
import sash7 from "@/assets/sash-template-7.png";
import sash8 from "@/assets/sash-template-8.png";
import sash9 from "@/assets/sash-template-9.png";
import sash10 from "@/assets/sash-template-10.png";
import sash11 from "@/assets/sash-template-11.png";
import sash12 from "@/assets/sash-template-12.png";
import sash13 from "@/assets/sash-template-13.png";
import sash14 from "@/assets/sash-template-14.png";
import sash15 from "@/assets/sash-template-15.png";
import sash16 from "@/assets/sash-template-16.png";
import sash17 from "@/assets/sash-template-17.png";
import sash18 from "@/assets/sash-template-18.png";
import sash19 from "@/assets/sash-template-19.png";

interface Symbol {
  id: string;
  name: string;
  meaning: string;
  image: string;
  category: "adinkra" | "other";
}

interface TextBox {
  id: string;
  text: string;
  color: string;
  left: number;
  top: number;
  fontSize: number;
}

interface TemplateSymbol {
  position: "left" | "right";
  symbol: Symbol;
  useInscriptionPosition?: boolean; // Flag to indicate if symbol should use inscription position instead of logo position
}

interface TemplateInscription {
  position: "left" | "right";
  text: string;
  useLogoPosition?: boolean; // Flag to indicate if text should use logo position instead of inscription position
}

interface KenteCanvasProps {
  selectedPattern: string;
  textBoxes: TextBox[];
  symbols: Symbol[];
  templateSymbols?: TemplateSymbol[];
  templateInscriptions?: TemplateInscription[];
  onRemoveTextBox?: (textBoxId: string) => void;
  onRemoveSymbol?: (symbolId: string) => void;
  onRemoveTemplateSymbol?: (
    position: "left" | "right",
    useInscriptionPosition: boolean
  ) => void;
  onRemoveTemplateInscription?: (
    position: "left" | "right",
    useLogoPosition: boolean
  ) => void;
  canvasRef?: React.RefObject<FabricCanvas | null>;
  showPositioningGuides?: boolean;
}

const sashMap: Record<string, string> = {
  "sash-1": sash1,
  "sash-2": sash2,
  "sash-3": sash3,
  "sash-4": sash4,
  "sash-5": sash5,
  "sash-6": sash6,
  "sash-7": sash7,
  "sash-8": sash8,
  "sash-9": sash9,
  "sash-10": sash10,
  "sash-11": sash11,
  "sash-12": sash12,
  "sash-13": sash13,
  "sash-14": sash14,
  "sash-15": sash15,
  "sash-16": sash16,
  "sash-17": sash17,
  "sash-18": sash18,
  "sash-19": sash19,
};

export const KenteCanvas = ({
  selectedPattern,
  textBoxes,
  symbols,
  templateSymbols = [],
  templateInscriptions = [],
  onRemoveTextBox,
  onRemoveSymbol,
  onRemoveTemplateSymbol,
  onRemoveTemplateInscription,
  canvasRef: externalCanvasRef,
  showPositioningGuides = false,
}: KenteCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<FabricCanvas | null>(null);

  // Use external ref if provided, otherwise use internal ref
  const canvasRefToUse = externalCanvasRef || fabricCanvasRef;

  // Ensure external ref is properly assigned
  useEffect(() => {
    if (externalCanvasRef && fabricCanvasRef.current) {
      externalCanvasRef.current = fabricCanvasRef.current;
    }
  }, [externalCanvasRef]);

  // Add positioning guides
  useEffect(() => {
    if (!canvasRefToUse.current || !showPositioningGuides) return;

    const canvas = canvasRefToUse.current;
    const positions = getTemplatePositions(selectedPattern);

    // Remove existing guides
    const objects = canvas.getObjects();
    const existingGuides = objects.filter(
      (obj) =>
        (obj as unknown as { isPositioningGuide?: boolean })
          .isPositioningGuide === true
    );
    existingGuides.forEach((obj) => canvas.remove(obj));

    // Calculate responsive scaling for mobile
    const isMobile = window.innerWidth < 768;
    const canvasWidth = canvas.width || 600;
    const canvasHeight = canvas.height || 800;
    const scaleFactor = isMobile
      ? Math.min(canvasWidth / 600, canvasHeight / 800)
      : 1;

    // Add positioning guides
    const addGuide = (
      position: any,
      type: "logo" | "inscription",
      side: "left" | "right"
    ) => {
      if (!position) return;

      // Apply responsive scaling
      const scaledX = position.x * scaleFactor;
      const scaledY = position.y * scaleFactor;
      const scaledWidth = position.width * scaleFactor;
      const scaledHeight = position.height * scaleFactor;

      const guide = new Rect({
        left: scaledX,
        top: scaledY,
        width: scaledWidth,
        height: scaledHeight,
        fill: "transparent",
        stroke: type === "logo" ? "#ff0000" : "#00ff00",
        strokeWidth: isMobile ? 1 : 2,
        strokeDashArray: [5, 5],
        selectable: true,
        evented: true,
        hoverCursor: "move",
        hasControls: true,
        hasBorders: true,
        lockRotation: true,
        lockScalingX: false,
        lockScalingY: false,
        lockMovementX: false,
        lockMovementY: false,
      });

      // Add label
      const label = new Text(
        `${side.toUpperCase()} ${type.toUpperCase()}\nX:${position.x} Y:${
          position.y
        }\nW:${position.width} H:${position.height}`,
        {
          left: scaledX,
          top: scaledY - (isMobile ? 20 : 30),
          fontSize: isMobile ? 10 : 12,
          fill: type === "logo" ? "#ff0000" : "#00ff00",
          fontFamily: "Arial",
          selectable: false,
          evented: false,
          hoverCursor: "default",
        }
      );

      // Store guide metadata
      (guide as any).isPositioningGuide = true;
      (guide as any).guideType = type;
      (guide as any).guideSide = side;
      (guide as any).label = label;
      (label as any).isPositioningGuide = true;
      (label as any).guide = guide;

      canvas.add(guide);
      canvas.add(label);
    };

    // Add guides for left and right positions
    if (positions.left) {
      if (positions.left.logo) addGuide(positions.left.logo, "logo", "left");
      if (positions.left.inscription)
        addGuide(positions.left.inscription, "inscription", "left");
    }
    if (positions.right) {
      if (positions.right.logo) addGuide(positions.right.logo, "logo", "right");
      if (positions.right.inscription)
        addGuide(positions.right.inscription, "inscription", "right");
    }

    // Add event handlers for guide movement and resizing
    const handleGuideMoving = (e: any) => {
      const guide = e.target;
      if (!guide.isPositioningGuide) return;

      const label = guide.label;
      if (label) {
        const x = Math.round(guide.left || 0);
        const y = Math.round(guide.top || 0);
        const w = Math.round(guide.width || 0);
        const h = Math.round(guide.height || 0);

        label.set({
          text: `${guide.guideSide.toUpperCase()} ${guide.guideType.toUpperCase()}\nX:${x} Y:${y}\nW:${w} H:${h}`,
          left: x,
          top: y - 30,
        });

        canvas.renderAll();
      }
    };

    const handleGuideScaling = (e: any) => {
      const guide = e.target;
      if (!guide.isPositioningGuide) return;

      const label = guide.label;
      if (label) {
        const x = Math.round(guide.left || 0);
        const y = Math.round(guide.top || 0);
        const w = Math.round((guide.width || 0) * (guide.scaleX || 1));
        const h = Math.round((guide.height || 0) * (guide.scaleY || 1));

        label.set({
          text: `${guide.guideSide.toUpperCase()} ${guide.guideType.toUpperCase()}\nX:${x} Y:${y}\nW:${w} H:${h}`,
          left: x,
          top: y - 30,
        });

        canvas.renderAll();
      }
    };

    // Add event listeners for guide interactions
    canvas.on("object:moving", handleGuideMoving);
    canvas.on("object:scaling", handleGuideScaling);

    canvas.renderAll();

    // Cleanup function
    return () => {
      canvas.off("object:moving", handleGuideMoving);
      canvas.off("object:scaling", handleGuideScaling);
    };
  }, [showPositioningGuides, selectedPattern, canvasRefToUse]);

  // Add real-time position tracking
  useEffect(() => {
    if (!canvasRefToUse.current || !showPositioningGuides) return;

    const canvas = canvasRefToUse.current;
    let positionInfoText: any = null;

    const updatePositionInfo = (obj: any) => {
      if (!obj) {
        // Remove position info if no object selected
        if (positionInfoText) {
          canvas.remove(positionInfoText);
          positionInfoText = null;
          canvas.renderAll();
        }
        return;
      }

      const left = Math.round(obj.left || 0);
      const top = Math.round(obj.top || 0);
      const width = Math.round((obj.width || 0) * (obj.scaleX || 1));
      const height = Math.round((obj.height || 0) * (obj.scaleY || 1));
      const scale = Math.round((obj.scaleX || 1) * 100);

      console.log(
        `Object moved/resized: X:${left} Y:${top} W:${width} H:${height}`
      );

      // Remove existing position info
      if (positionInfoText) {
        canvas.remove(positionInfoText);
      }

      // Create new position info text with copy button
      const isMobile = window.innerWidth < 768;
      const positionInfo = `Selected Object:\nX: ${left}px\nY: ${top}px\nWidth: ${width}px\nHeight: ${height}px\nScale: ${scale}%`;
      const copyButtonText = `[COPY]`;

      positionInfoText = new Text(`${positionInfo}\n\n${copyButtonText}`, {
        left: isMobile ? 10 : 20,
        top: isMobile ? 10 : 20,
        fontSize: isMobile ? 12 : 14,
        fill: "#ffffff",
        fontFamily: "Arial",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: isMobile ? 6 : 8,
        selectable: false,
        evented: true,
        hoverCursor: "pointer",
        originX: "left",
        originY: "top",
      });

      // Add click handler for copy functionality
      positionInfoText.on("mousedown", () => {
        const copyText = `{ x: ${left}, y: ${top}, width: ${width}, height: ${height} }`;
        navigator.clipboard
          .writeText(copyText)
          .then(() => {
            // Show temporary success message
            const successText = new Text(`✅ COPIED!\n${copyText}`, {
              left: isMobile ? 10 : 20,
              top: isMobile ? 10 : 20,
              fontSize: isMobile ? 10 : 12,
              fill: "#00ff00",
              fontFamily: "Arial",
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              padding: isMobile ? 4 : 6,
              selectable: false,
              evented: false,
              hoverCursor: "default",
              originX: "left",
              originY: "top",
            });

            canvas.add(successText);
            canvas.renderAll();

            // Remove success message after 2 seconds
            setTimeout(() => {
              canvas.remove(successText);
              canvas.renderAll();
            }, 2000);
          })
          .catch(() => {
            // Fallback if clipboard fails
            alert(
              `Position values:\nX: ${left}, Y: ${top}, W: ${width}, H: ${height}`
            );
          });
      });

      canvas.add(positionInfoText);
      canvas.renderAll();
    };

    const handleObjectMoving = (e: any) => {
      updatePositionInfo(e.target);
    };

    const handleObjectScaling = (e: any) => {
      updatePositionInfo(e.target);
    };

    const handleObjectRotating = (e: any) => {
      updatePositionInfo(e.target);
    };

    const handleSelectionCreated = (e: any) => {
      updatePositionInfo(e.selected[0]);
    };

    const handleSelectionUpdated = (e: any) => {
      updatePositionInfo(e.selected[0]);
    };

    const handleSelectionCleared = () => {
      updatePositionInfo(null);
    };

    // Add event listeners
    canvas.on("object:moving", handleObjectMoving);
    canvas.on("object:scaling", handleObjectScaling);
    canvas.on("object:rotating", handleObjectRotating);
    canvas.on("selection:created", handleSelectionCreated);
    canvas.on("selection:updated", handleSelectionUpdated);
    canvas.on("selection:cleared", handleSelectionCleared);

    return () => {
      // Cleanup event listeners
      canvas.off("object:moving", handleObjectMoving);
      canvas.off("object:scaling", handleObjectScaling);
      canvas.off("object:rotating", handleObjectRotating);
      canvas.off("selection:created", handleSelectionCreated);
      canvas.off("selection:updated", handleSelectionUpdated);
      canvas.off("selection:cleared", handleSelectionCleared);

      // Remove position info text when guides are turned off
      if (positionInfoText) {
        canvas.remove(positionInfoText);
        canvas.renderAll();
      }
    };
  }, [showPositioningGuides, canvasRefToUse]);

  const textboxRefs = useRef<Map<string, Textbox>>(new Map());

  // Initialize canvas only once
  useEffect(() => {
    if (!canvasRef.current) return;

    // Responsive canvas dimensions
    const canvasWidth = Math.min(600, window.innerWidth - 32); // Account for padding
    const canvasHeight = Math.min(800, canvasWidth * 1.33); // Maintain aspect ratio

    const canvas = new FabricCanvas(canvasRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: "#ffffff",
    });

    canvasRefToUse.current = canvas;

    // Add double-click to edit text functionality (like Canva)
    canvas.on("mouse:dblclick", (e) => {
      const activeObject = e.target;
      if (
        activeObject &&
        (activeObject as unknown as { isTextbox?: boolean }).isTextbox
      ) {
        // Enter text editing mode
        activeObject.enterEditing();
      }
    });

    // Add selection change listener to show/hide delete button
    canvas.on("selection:created", (e) => {
      const activeObject = e.selected?.[0];
      if (
        activeObject &&
        !(activeObject as unknown as { sashImage?: boolean }).sashImage
      ) {
        addDeleteButton(activeObject);
      }
    });

    canvas.on("selection:updated", (e) => {
      const activeObject = e.selected?.[0];
      if (
        activeObject &&
        !(activeObject as unknown as { sashImage?: boolean }).sashImage
      ) {
        addDeleteButton(activeObject);
      }
    });

    canvas.on("selection:cleared", () => {
      removeDeleteButton();
    });

    // Function to add delete button
    const addDeleteButton = (obj: any) => {
      // Remove existing delete button
      removeDeleteButton();

      // Create delete button
      const deleteBtn = document.createElement("div");
      deleteBtn.id = "delete-btn";
      deleteBtn.innerHTML = "×";
      deleteBtn.style.cssText = `
        position: absolute;
        width: 20px;
        height: 20px;
        background: #ff4444;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: bold;
        cursor: pointer;
        z-index: 1000;
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        user-select: none;
        border: 1px solid white;
        transition: all 0.2s ease;
      `;

      // Position delete button
      const updateDeleteButtonPosition = () => {
        const objCoords = obj.getCoords();
        const canvasEl = canvasRef.current;
        if (canvasEl && objCoords) {
          const canvasRect = canvasEl.getBoundingClientRect();
          const scaleX = canvasEl.width / canvasEl.offsetWidth;
          const scaleY = canvasEl.height / canvasEl.offsetHeight;

          // Position at top-right corner of the object
          const objWidth = (objCoords[1].x - objCoords[0].x) / scaleX;
          const objHeight = (objCoords[2].y - objCoords[0].y) / scaleY;

          deleteBtn.style.left =
            canvasRect.left +
            objCoords[1].x / scaleX +
            objWidth / 2 -
            10 +
            "px";
          deleteBtn.style.top =
            canvasRect.top +
            objCoords[0].y / scaleY -
            objHeight / 2 -
            10 +
            "px";
        }
      };

      updateDeleteButtonPosition();
      document.body.appendChild(deleteBtn);

      // Add click handler
      deleteBtn.onclick = (e) => {
        e.stopPropagation();

        // Check if it's a template symbol or template inscription
        if (
          (obj as unknown as { isTemplateSymbol?: boolean }).isTemplateSymbol
        ) {
          const templatePosition = (
            obj as unknown as { templatePosition?: string }
          ).templatePosition;
          const useInscriptionPosition = (
            obj as unknown as { useInscriptionPosition?: boolean }
          ).useInscriptionPosition;

          // Call the template symbol removal callback
          if (templatePosition && onRemoveTemplateSymbol) {
            onRemoveTemplateSymbol(
              templatePosition as "left" | "right",
              useInscriptionPosition || false
            );
          }
        } else if (
          (obj as unknown as { isTemplateInscription?: boolean })
            .isTemplateInscription
        ) {
          const templatePosition = (
            obj as unknown as { templatePosition?: string }
          ).templatePosition;
          const useLogoPosition = (
            obj as unknown as { useLogoPosition?: boolean }
          ).useLogoPosition;

          // Call the template inscription removal callback
          if (templatePosition && onRemoveTemplateInscription) {
            onRemoveTemplateInscription(
              templatePosition as "left" | "right",
              useLogoPosition || false
            );
          }
        } else if ((obj as unknown as { isTextbox?: boolean }).isTextbox) {
          // Handle regular text boxes
          const textBoxId = (obj as unknown as { textBoxId?: string })
            .textBoxId;
          if (textBoxId && onRemoveTextBox) {
            onRemoveTextBox(textBoxId);
          }
        } else if ((obj as unknown as { isSymbol?: boolean }).isSymbol) {
          // Handle regular symbols
          const symbolId = (obj as unknown as { symbolId?: string }).symbolId;
          if (symbolId && onRemoveSymbol) {
            onRemoveSymbol(symbolId);
          }
        }

        canvas.remove(obj);
        canvas.discardActiveObject();
        canvas.renderAll();
        removeDeleteButton();
      };

      // Update position on object move
      obj.on("moving", updateDeleteButtonPosition);
      obj.on("scaling", updateDeleteButtonPosition);
      obj.on("rotating", updateDeleteButtonPosition);
    };

    // Function to remove delete button
    const removeDeleteButton = () => {
      const existingBtn = document.getElementById("delete-btn");
      if (existingBtn) {
        existingBtn.remove();
      }
    };

    return () => {
      // Clean up delete button
      const existingBtn = document.getElementById("delete-btn");
      if (existingBtn) {
        existingBtn.remove();
      }
      canvas.dispose();
    };
  }, []); // Remove text dependency

  // Handle text boxes updates
  useEffect(() => {
    if (!canvasRefToUse.current) return;

    const canvas = canvasRefToUse.current;

    // Get existing text boxes on canvas
    const objects = canvas.getObjects();
    const existingTextboxes = objects.filter(
      (obj) => (obj as unknown as { isTextbox?: boolean }).isTextbox === true
    );

    // Add only new text boxes (don't reposition existing ones)
    textBoxes.forEach((textBox) => {
      // Check if this text box already exists on canvas
      const existingTextbox = existingTextboxes.find(
        (obj) =>
          (obj as unknown as { textBoxId?: string }).textBoxId === textBox.id
      );

      if (!existingTextbox) {
        // Only add if it doesn't already exist

        // Calculate optimal font size for regular text boxes with smart word wrapping
        const calculateOptimalFontSizeForTextBox = (
          text: string,
          maxWidth: number = 200,
          fontFamily: string = "Arial"
        ) => {
          let fontSize = 16; // Start with a reasonable font size
          const minFontSize = 8; // Minimum readable font size

          // Create a temporary canvas to measure text
          const tempCanvas = document.createElement("canvas");
          const tempCtx = tempCanvas.getContext("2d");
          if (!tempCtx) return fontSize;

          // Check if text fits on one line first
          tempCtx.font = `${fontSize}px ${fontFamily}`;
          let textWidth = tempCtx.measureText(text).width;

          // If text fits on one line, we're good
          if (textWidth <= maxWidth) {
            return fontSize;
          }

          // If text doesn't fit, try to fit with word wrapping
          const words = text.split(" ");
          if (words.length > 1) {
            // Calculate font size that allows text to wrap at word boundaries
            let bestFontSize = fontSize;

            // Try reducing font size until text fits with word wrapping
            while (bestFontSize > minFontSize) {
              tempCtx.font = `${bestFontSize}px ${fontFamily}`;

              // Check if longest word fits on one line
              const longestWord = words.reduce((longest, word) =>
                word.length > longest.length ? word : longest
              );
              const longestWordWidth = tempCtx.measureText(longestWord).width;

              // Check if text fits when wrapped at word boundaries
              let currentLineWidth = 0;
              let fitsWithWrapping = true;

              for (const word of words) {
                const wordWidth = tempCtx.measureText(word + " ").width;

                if (currentLineWidth + wordWidth > maxWidth) {
                  // Start new line
                  currentLineWidth = wordWidth;
                  // Check if this word fits on its own line
                  if (tempCtx.measureText(word).width > maxWidth) {
                    fitsWithWrapping = false;
                    break;
                  }
                } else {
                  currentLineWidth += wordWidth;
                }
              }

              if (fitsWithWrapping && longestWordWidth <= maxWidth) {
                return bestFontSize;
              }

              bestFontSize -= 1;
            }
          } else {
            // Single word - just reduce font size until it fits
            while (textWidth > maxWidth && fontSize > minFontSize) {
              fontSize -= 1;
              tempCtx.font = `${fontSize}px ${fontFamily}`;
              textWidth = tempCtx.measureText(text).width;
            }
            return Math.max(fontSize, minFontSize);
          }

          return Math.max(fontSize, minFontSize);
        };

        const optimalFontSize = calculateOptimalFontSizeForTextBox(
          textBox.text,
          200,
          "Arial"
        );

        const fabricTextbox = new Textbox(textBox.text, {
          left: textBox.left,
          top: textBox.top,
          width: 200,
          fontSize: optimalFontSize,
          fill: textBox.color,
          fontFamily: "Arial",
          textAlign: "center",
          fontWeight: "bold",
          selectable: true,
          hasControls: true,
          hasBorders: true,
          splitByGrapheme: false, // Allow word wrapping at spaces
          lineHeight: 1.2, // Better line height for multi-line text
        });

        (fabricTextbox as unknown as { isTextbox?: boolean }).isTextbox = true;
        (fabricTextbox as unknown as { textBoxId?: string }).textBoxId =
          textBox.id;

        // Add event listener to recalculate font size when text is modified
        fabricTextbox.on("modified", () => {
          const newText = fabricTextbox.text || "";
          if (newText.trim()) {
            const newOptimalFontSize = calculateOptimalFontSizeForTextBox(
              newText,
              200,
              "Arial"
            );
            fabricTextbox.set("fontSize", newOptimalFontSize);
            canvas.renderAll();
          }
        });

        textboxRefs.current.set(textBox.id, fabricTextbox);
        canvas.add(fabricTextbox);
      }
    });

    canvas.renderAll();
  }, [textBoxes]);

  // Update sash template when selection changes
  useEffect(() => {
    if (!canvasRefToUse.current) return;

    const canvas = canvasRefToUse.current;
    const sashUrl = sashMap[selectedPattern];

    // Remove existing sash images and backgrounds if any
    const objects = canvas.getObjects();
    const existingSashElements = objects.filter(
      (obj) => (obj as unknown as { sashImage?: boolean }).sashImage === true
    );
    existingSashElements.forEach((obj) => canvas.remove(obj));

    // Load and add new sash template
    FabricImage.fromURL(sashUrl).then((img) => {
      const currentCanvasWidth = canvas.width || 600;
      const currentCanvasHeight = canvas.height || 800;
      const scale =
        Math.min(
          currentCanvasWidth / (img.width || 1),
          currentCanvasHeight / (img.height || 1)
        ) * 0.85;

      img.set({
        left: currentCanvasWidth / 2,
        top: currentCanvasHeight / 2,
        scaleX: scale,
        scaleY: scale,
        originX: "center",
        originY: "center",
        selectable: false,
      });

      (img as unknown as { sashImage?: boolean }).sashImage = true;

      // Add white background behind the sash for templates with dot patterns
      if (selectedPattern === "sash-2" || selectedPattern === "sash-3") {
        const whiteBackground = new Rect({
          left: currentCanvasWidth / 2,
          top: currentCanvasHeight / 2,
          width: (img.width || 1) * scale,
          height: (img.height || 1) * scale,
          fill: "white",
          originX: "center",
          originY: "center",
          selectable: false,
          evented: false,
        });

        (whiteBackground as unknown as { sashImage?: boolean }).sashImage =
          true;
        // Insert background first (behind the sash)
        canvas.insertAt(0, whiteBackground);
        // Insert sash on top of background
        canvas.insertAt(1, img);
      } else {
        // For other templates, just add the sash normally
        canvas.insertAt(0, img);
      }

      canvas.renderAll();
    });
  }, [selectedPattern]);

  // Handle symbol addition and updates
  useEffect(() => {
    if (!canvasRefToUse.current) return;

    const canvas = canvasRefToUse.current;

    // Get existing symbols on canvas
    const objects = canvas.getObjects();
    const existingSymbols = objects.filter(
      (obj) => (obj as unknown as { isSymbol?: boolean }).isSymbol === true
    );

    // Add only new symbols (don't reposition existing ones)
    symbols.forEach((symbol) => {
      // Check if this symbol already exists on canvas
      const existingSymbol = existingSymbols.find(
        (obj) =>
          (obj as unknown as { symbolId?: string }).symbolId === symbol.id
      );

      if (!existingSymbol) {
        // Only add if it doesn't already exist
        FabricImage.fromURL(symbol.image).then((img) => {
          // Check if this is a template symbol (has position info)
          const positions = getTemplatePositions(selectedPattern);
          let x, y, scaleX, scaleY;

          // Check if this symbol should be positioned as a template symbol
          const templateSymbol = templateSymbols.find(
            (ts) => ts.symbol.id === symbol.id
          );
          if (templateSymbol && positions[templateSymbol.position]?.logo) {
            // Use template positioning with responsive scaling
            const isMobile = window.innerWidth < 768;
            const canvasWidth = canvas.width || 600;
            const canvasHeight = canvas.height || 800;
            const scaleFactor = isMobile
              ? Math.min(canvasWidth / 600, canvasHeight / 800)
              : 1;

            const position = positions[templateSymbol.position].logo!;

            // Apply responsive scaling to position
            const scaledX = position.x * scaleFactor;
            const scaledY = position.y * scaleFactor;
            const scaledWidth = position.width * scaleFactor;
            const scaledHeight = position.height * scaleFactor;

            // Calculate scale to fit within the designated area
            const scaleXCalc = scaledWidth / (img.width || 100);
            const scaleYCalc = scaledHeight / (img.height || 100);
            const scale = Math.min(scaleXCalc, scaleYCalc);

            // Calculate actual dimensions after scaling
            const actualWidth = (img.width || 100) * scale;
            const actualHeight = (img.height || 100) * scale;

            // Center the symbol within the bounding box
            x = scaledX + (scaledWidth - actualWidth) / 2;
            y = scaledY + (scaledHeight - actualHeight) / 2;
            scaleX = scaleY = scale;
          } else {
            // Use responsive symbol positioning - place new symbols in a staggered pattern
            const canvasWidth = canvas.width || 600;
            const canvasHeight = canvas.height || 800;
            const symbolSize = Math.min(60, canvasWidth * 0.1);

            // Calculate position for new symbol to avoid overlap with existing ones
            const newSymbolIndex = symbols.findIndex((s) => s.id === symbol.id);

            if (existingSymbols.length === 0) {
              // First symbol - place in center-left
              x = canvasWidth * 0.1;
              y = canvasHeight * 0.3;
              scaleX = scaleY = symbolSize / 100;
            } else {
              // Place new symbols in a staggered pattern to avoid overlap
              const offsetX = (newSymbolIndex % 3) * 80;
              const offsetY = Math.floor(newSymbolIndex / 3) * 80;
              x = canvasWidth * 0.1 + offsetX;
              y = canvasHeight * 0.3 + offsetY;
              scaleX = scaleY = symbolSize / 100;
            }
          }

          img.set({
            left: x,
            top: y,
            scaleX: scaleX,
            scaleY: scaleY,
            originX: "left",
            originY: "top",
            selectable: true,
            hasControls: true,
            hasBorders: true,
          });

          (img as unknown as { isSymbol?: boolean }).isSymbol = true;
          (img as unknown as { symbolId?: string }).symbolId = symbol.id;

          canvas.add(img);
          canvas.renderAll();
        });
      }
    });
  }, [symbols]);

  // Handle template symbols
  useEffect(() => {
    if (!canvasRefToUse.current) return;

    const canvas = canvasRefToUse.current;
    const positions = getTemplatePositions(selectedPattern);

    // Remove existing template symbols
    const objects = canvas.getObjects();
    const existingTemplateSymbols = objects.filter(
      (obj) =>
        (obj as unknown as { isTemplateSymbol?: boolean }).isTemplateSymbol ===
        true
    );
    existingTemplateSymbols.forEach((obj) => canvas.remove(obj));

    // Add new template symbols
    templateSymbols.forEach((templateSymbol) => {
      const position = positions[templateSymbol.position];

      // Determine which position to use: inscription position or logo position
      const targetPosition = templateSymbol.useInscriptionPosition
        ? position?.inscription
        : position?.logo;

      if (targetPosition) {
        FabricImage.fromURL(templateSymbol.symbol.image).then((img) => {
          // Calculate responsive scaling for mobile
          const isMobile = window.innerWidth < 768;
          const canvasWidth = canvas.width || 600;
          const canvasHeight = canvas.height || 800;
          const scaleFactor = isMobile
            ? Math.min(canvasWidth / 600, canvasHeight / 800)
            : 1;

          // Apply responsive scaling to position
          const scaledX = targetPosition.x * scaleFactor;
          const scaledY = targetPosition.y * scaleFactor;
          const scaledWidth = targetPosition.width * scaleFactor;
          const scaledHeight = targetPosition.height * scaleFactor;

          // Calculate scale to fit within the designated area
          const scaleX = scaledWidth / (img.width || 100);
          const scaleY = scaledHeight / (img.height || 100);

          // Use the smaller scale to maintain aspect ratio
          const scale = Math.min(scaleX, scaleY);

          // Calculate actual dimensions after scaling
          const actualWidth = (img.width || 100) * scale;
          const actualHeight = (img.height || 100) * scale;

          // Center the symbol within the bounding box
          const centerX = scaledX + (scaledWidth - actualWidth) / 2;
          const centerY = scaledY + (scaledHeight - actualHeight) / 2;

          img.set({
            left: centerX,
            top: centerY,
            scaleX: scale,
            scaleY: scale,
            originX: "left",
            originY: "top",
            selectable: true,
            hasControls: true,
            hasBorders: true,
          });

          (img as unknown as { isTemplateSymbol?: boolean }).isTemplateSymbol =
            true;
          (img as unknown as { templatePosition?: string }).templatePosition =
            templateSymbol.position;
          (img as unknown as { symbolId?: string }).symbolId =
            templateSymbol.symbol.id;
          (
            img as unknown as { useInscriptionPosition?: boolean }
          ).useInscriptionPosition =
            templateSymbol.useInscriptionPosition || false;

          canvas.add(img);
          canvas.renderAll();
        });
      }
    });
  }, [templateSymbols, selectedPattern]);

  // Handle template inscriptions
  useEffect(() => {
    if (!canvasRefToUse.current) return;

    const canvas = canvasRefToUse.current;
    const positions = getTemplatePositions(selectedPattern);

    // Remove existing template inscriptions
    const objects = canvas.getObjects();
    const existingTemplateInscriptions = objects.filter(
      (obj) =>
        (obj as unknown as { isTemplateInscription?: boolean })
          .isTemplateInscription === true
    );
    existingTemplateInscriptions.forEach((obj) => canvas.remove(obj));

    // Add new template inscriptions
    templateInscriptions.forEach((templateInscription) => {
      const position = positions[templateInscription.position];

      // Determine which position to use: logo position or inscription position
      const targetPosition = templateInscription.useLogoPosition
        ? position?.logo
        : position?.inscription;

      if (targetPosition && templateInscription.text.trim()) {
        // Calculate responsive scaling for mobile
        const isMobile = window.innerWidth < 768;
        const canvasWidth = canvas.width || 600;
        const canvasHeight = canvas.height || 800;
        const scaleFactor = isMobile
          ? Math.min(canvasWidth / 600, canvasHeight / 800)
          : 1;

        // Apply responsive scaling to position
        const scaledX = targetPosition.x * scaleFactor;
        const scaledY = targetPosition.y * scaleFactor;
        const scaledWidth = targetPosition.width * scaleFactor;
        const scaledHeight = targetPosition.height * scaleFactor;
        const scaledFontSize = (targetPosition as any).fontSize
          ? (targetPosition as any).fontSize * scaleFactor
          : Math.min(scaledWidth, scaledHeight) * 0.3; // Default fontSize for logo positions

        // Calculate optimal font size with smart word wrapping
        const calculateOptimalFontSize = (
          text: string,
          maxWidth: number,
          maxHeight: number,
          fontFamily: string = "Arial"
        ) => {
          // Start with a reasonable font size
          let fontSize = Math.min(maxWidth, maxHeight) * 0.3;
          const minFontSize = 8; // Minimum readable font size

          // Create a temporary canvas to measure text
          const tempCanvas = document.createElement("canvas");
          const tempCtx = tempCanvas.getContext("2d");
          if (!tempCtx) return fontSize;

          // Check if text fits on one line first
          tempCtx.font = `${fontSize}px ${fontFamily}`;
          let textWidth = tempCtx.measureText(text).width;

          // If text fits on one line, we're good
          if (textWidth <= maxWidth) {
            return fontSize;
          }

          // If text doesn't fit, try to fit with word wrapping
          const words = text.split(" ");
          if (words.length > 1) {
            // Calculate font size that allows text to wrap at word boundaries
            let bestFontSize = fontSize;

            // Try reducing font size until text fits with word wrapping
            while (bestFontSize > minFontSize) {
              tempCtx.font = `${bestFontSize}px ${fontFamily}`;

              // Check if longest word fits on one line
              const longestWord = words.reduce((longest, word) =>
                word.length > longest.length ? word : longest
              );
              const longestWordWidth = tempCtx.measureText(longestWord).width;

              // Check if text fits when wrapped at word boundaries
              let currentLineWidth = 0;
              let fitsWithWrapping = true;

              for (const word of words) {
                const wordWidth = tempCtx.measureText(word + " ").width;

                if (currentLineWidth + wordWidth > maxWidth) {
                  // Start new line
                  currentLineWidth = wordWidth;
                  // Check if this word fits on its own line
                  if (tempCtx.measureText(word).width > maxWidth) {
                    fitsWithWrapping = false;
                    break;
                  }
                } else {
                  currentLineWidth += wordWidth;
                }
              }

              if (fitsWithWrapping && longestWordWidth <= maxWidth) {
                return bestFontSize;
              }

              bestFontSize -= 1;
            }
          } else {
            // Single word - just reduce font size until it fits
            while (textWidth > maxWidth && fontSize > minFontSize) {
              fontSize -= 1;
              tempCtx.font = `${fontSize}px ${fontFamily}`;
              textWidth = tempCtx.measureText(text).width;
            }
            return Math.max(fontSize, minFontSize);
          }

          return Math.max(fontSize, minFontSize);
        };

        // Calculate optimal font size for single line
        const optimalFontSize = calculateOptimalFontSize(
          templateInscription.text,
          scaledWidth,
          scaledHeight,
          "Arial"
        );

        // Create a Textbox for better text wrapping and editing
        const fabricTextbox = new Textbox(templateInscription.text, {
          left: scaledX,
          top: scaledY,
          width: scaledWidth,
          height: scaledHeight,
          fontSize: optimalFontSize,
          fontFamily: "Arial",
          fill: "#FFD700", // Default yellow color
          textAlign: "center", // Center text horizontally
          originX: "left",
          originY: "top",
          selectable: true,
          hasControls: true,
          hasBorders: true,
          splitByGrapheme: false, // Allow word wrapping at spaces
          charSpacing: 0,
          lineHeight: 1.2, // Better line height for multi-line text
          verticalAlign: "middle", // Center text vertically
          centeredScaling: false,
          centeredRotation: false,
        });

        // Calculate and set the center position for vertical alignment
        const centerY = scaledY + scaledHeight / 2;
        fabricTextbox.set({
          top: centerY,
          originY: "center", // Change origin to center for proper vertical centering
        });

        (
          fabricTextbox as unknown as { isTemplateInscription?: boolean }
        ).isTemplateInscription = true;
        (
          fabricTextbox as unknown as { templatePosition?: string }
        ).templatePosition = templateInscription.position;
        (
          fabricTextbox as unknown as { useLogoPosition?: boolean }
        ).useLogoPosition = templateInscription.useLogoPosition || false;

        // Add event listener to recalculate font size when text is modified
        fabricTextbox.on("modified", () => {
          const newText = fabricTextbox.text || "";
          if (newText.trim()) {
            const newOptimalFontSize = calculateOptimalFontSize(
              newText,
              scaledWidth,
              scaledHeight,
              "Arial"
            );
            fabricTextbox.set("fontSize", newOptimalFontSize);
            canvas.renderAll();
          }
        });

        canvas.add(fabricTextbox);
        canvas.renderAll();
      }
    });
  }, [templateInscriptions, selectedPattern]);

  // Handle window resize for responsive canvas
  useEffect(() => {
    const handleResize = () => {
      if (!canvasRefToUse.current) return;

      const canvas = canvasRefToUse.current;
      const newWidth = Math.min(600, window.innerWidth - 32);
      const newHeight = Math.min(800, newWidth * 1.33);

      canvas.setDimensions({
        width: newWidth,
        height: newHeight,
      });

      canvas.renderAll();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="border-2 border-border rounded-lg overflow-hidden shadow-lg w-full h-full flex items-center justify-center relative">
      <canvas
        ref={canvasRef}
        className="w-full h-auto max-w-full touch-manipulation"
      />
    </div>
  );
};
